// InscribedTriangleDiagram.js
import React from "react";
import { InlineMath } from "react-katex";

/**
 * InscribedTriangleDiagram (dynamiczny)
 * - Trójkąt ABC czarno/biały.
 * - α_C (niewiadomy) – łuk + podpis pomarańczowe.
 * - P₁ (fiolet) i P₂ (zielony) – ramiona + łuk + punkt + literka (bez wartości).
 *
 * Kolizje:
 *  - minArcGapPx: minimalny dystans od A/B/C oraz P₁↔P₂
 *  - gapMode: "arc" (po łuku, domyślnie) lub "chord" (odległość prosta/cięciwa)
 */

export default function InscribedTriangleDiagram({
  size = 270,
  circleR = 104,

  // podpisy kątów przy wierzchołkach (używamy tylko labelC)
  labelA = "",
  labelB = "",
  labelC = "",

  // kąty wewnętrzne trójkąta (deg) — jeśli podane, determinują pozycje A,B,C
  anglesDegA,
  anglesDegB,
  anglesDegC,
  baseAngle = -20, // startowa pozycja A (deg)

  // offset podpisów (piksele) – faktycznie używamy tylko dla α_C
  angleLabelOffset = 18,
  angleLabelOffsetA,
  angleLabelOffsetB,
  angleLabelOffsetC,

  // konfiguracja P₁ / P₂
  showP1 = false,
  p1Arc = "BC",     // 'BC' | 'CA' | 'AB' – łuk (czyli cięciwa), na którym opiera się kąt w P₁
  p1ShiftDeg = 40,  // przesunięcie po okręgu względem wierzchołka „naprzeciw” łuku
  showP2 = false,
  p2Arc = "CA",
  p2ShiftDeg = -40,

  // antykolizja
  minArcGapPx,        // minimalny dystans (px); domyślnie = circleR
  gapMode = "arc",    // "arc" = dystans po łuku, "chord" = dystans prosto (cięciwa)
}) {
  const cx = size / 2;
  const cy = size / 2;
  const deg2rad = (d) => (d * Math.PI) / 180;
  const rad2deg = (r) => (r * 180) / Math.PI;

  // --- minimalny odstęp -> w stopniach centralnych ---
  const GAP_PX = Number.isFinite(minArcGapPx) ? minArcGapPx : circleR; // domyślnie r
  const MIN_GAP_DEG =
    gapMode === "chord"
      ? rad2deg(2 * Math.asin(Math.min(1, GAP_PX / (2 * circleR))))
      : rad2deg(GAP_PX / circleR);

  // --- pozycje A,B,C ---
  let Aang, Bang, Cang;
  if (
    Number.isFinite(anglesDegA) &&
    Number.isFinite(anglesDegB) &&
    Number.isFinite(anglesDegC)
  ) {
    const stepAB = 2 * anglesDegC; // AB = 2*α_C
    const stepBC = 2 * anglesDegA; // BC = 2*α_A
    Aang = baseAngle;
    Bang = Aang + stepAB;
    Cang = Bang + stepBC;          // CA = 2*α_B z domknięcia
  } else {
    Aang = -20; Bang = 250; Cang = 150; // fallback
  }

  // pomocnicze
  const norm = (deg) => ((deg % 360) + 360) % 360;
  const pointAt = (angDeg) => ({
    x: cx + circleR * Math.cos(deg2rad(angDeg)),
    y: cy + circleR * Math.sin(deg2rad(angDeg)),
  });
  const smallAngleDiff = (a, b) => {
    let d = norm(a) - norm(b);
    while (d <= -180) d += 360;
    while (d > 180) d -= 360;
    return Math.abs(d);
  };

  // dla danego łuku („BC”, „CA”, „AB”) baza to wierzchołek naprzeciw łuku
  const baseForArcDeg = (arc) => (arc === "BC" ? Aang : arc === "CA" ? Bang : Cang);

  // punkty P₁/P₂ – pozycje startowe
  let P1ang = norm(baseForArcDeg(p1Arc) + p1ShiftDeg);
  let P2ang = norm(baseForArcDeg(p2Arc) + p2ShiftDeg);

  // --- „odpychanie” po okręgu (od A/B/C i wzajemnie) ---
  const repelFromSet = (ang, forbidden, minGapDeg, stepDeg = 6, maxIter = 60) => {
    let a = norm(ang);
    let iter = 0;
    while (iter < maxIter) {
      let nearest = null;
      let bestGap = 9999;
      for (const f of forbidden) {
        const g = smallAngleDiff(a, f);
        if (g < bestGap) { bestGap = g; nearest = f; }
      }
      if (bestGap >= minGapDeg) break;
      // wybierz kierunek, który powiększa różnicę do „nearest”
      let d = norm(nearest) - norm(a);
      while (d <= -180) d += 360;
      while (d > 180) d -= 360;
      const dir = d > 0 ? -1 : 1; // jeśli nearest „po plusie”, idź w lewo
      a = norm(a + dir * stepDeg);
      iter++;
    }
    return a;
  };

  const ABC = [Aang, Bang, Cang];
  P1ang = repelFromSet(P1ang, ABC, MIN_GAP_DEG);
  P2ang = repelFromSet(P2ang, [...ABC, P1ang], MIN_GAP_DEG);

  // współrzędne
  const { x: Ax, y: Ay } = pointAt(Aang);
  const { x: Bx, y: By } = pointAt(Bang);
  const { x: Cx, y: Cy } = pointAt(Cang);
  const P1 = pointAt(P1ang);
  const P2 = pointAt(P2ang);

  // --- łuki kątów ---
  const ARC_R = 22;
  function angleArc(vx, vy, x1, y1, x2, y2, labelOffsetPx) {
    const a1 = Math.atan2(y1 - vy, x1 - vx);
    const a2 = Math.atan2(y2 - vy, x2 - vx);
    let d = a2 - a1;
    while (d <= -Math.PI) d += 2 * Math.PI;
    while (d > Math.PI) d -= 2 * Math.PI;
    const sweep = d >= 0 ? 1 : 0;
    const mid = a1 + d / 2;
    const labelR = ARC_R + (Number.isFinite(labelOffsetPx) ? labelOffsetPx : 0);
    return {
      d: `M ${vx + ARC_R * Math.cos(a1)} ${vy + ARC_R * Math.sin(a1)}
          A ${ARC_R} ${ARC_R} 0 ${Math.abs(d) > Math.PI ? 1 : 0} ${sweep}
            ${vx + ARC_R * Math.cos(a2)} ${vy + ARC_R * Math.sin(a2)}`,
      midx: vx + labelR * Math.cos(mid),
      midy: vy + labelR * Math.sin(mid),
    };
  }

  const offA = (angleLabelOffsetA ?? angleLabelOffset);
  const offB = (angleLabelOffsetB ?? angleLabelOffset);
  const offC = (angleLabelOffsetC ?? angleLabelOffset);

  const arcC = angleArc(Cx, Cy, Ax, Ay, Bx, By, offC);

  const endsForArc = (arc) => {
    if (arc === "BC") return [[Bx, By], [Cx, Cy]];
    if (arc === "CA") return [[Cx, Cy], [Ax, Ay]];
    return [[Ax, Ay], [Bx, By]]; // "AB"
  };
  const [P1e1, P1e2] = endsForArc(p1Arc);
  const [P2e1, P2e2] = endsForArc(p2Arc);

  const arcP1 = angleArc(P1.x, P1.y, P1e1[0], P1e1[1], P1e2[0], P1e2[1], offA);
  const arcP2 = angleArc(P2.x, P2.y, P2e1[0], P2e1[1], P2e2[0], P2e2[1], offB);

  // podpis α_C (z kolorem)
  const LabelAngle = ({ x, y, pre, sub, text, color = "#f59e0b" }) => {
    const math = text || `${pre}_{${sub}}`;
    return (
      <foreignObject x={x - 36} y={y - 13} width="72" height="28">
        <div style={{ color, fontSize: 14, textAlign: "center", lineHeight: 1.2 }}>
          <InlineMath math={math} />
        </div>
      </foreignObject>
    );
  };


  const MathPointLabel = ({ x, y, math }) => (
    <foreignObject x={x - 22} y={y - 14} width="44" height="28">
      <div style={{ fontSize: 16, lineHeight: 1.2, textAlign: "center", color: "currentColor" }}>
        <InlineMath math={math} />
      </div>
    </foreignObject>
  );

  // literki na zewnątrz okręgu
  const outsideLabel = (vx, vy, offset = 14) => {
    const dx = vx - cx, dy = vy - cy;
    const L = Math.hypot(dx, dy) || 1;
    return { x: cx + (circleR + offset) * (dx / L), y: cy + (circleR + offset) * (dy / L) };
  };
  const Aout = outsideLabel(Ax, Ay);
  const Bout = outsideLabel(Bx, By);
  const Cout = outsideLabel(Cx, Cy);
  const P1out = outsideLabel(P1.x, P1.y);
  const P2out = outsideLabel(P2.x, P2.y);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* okrąg */}
      <circle cx={cx} cy={cy} r={circleR} fill="none" stroke="currentColor" strokeWidth="2" />

      {/* trójkąt ABC */}
      <polygon
        points={`${Ax},${Ay} ${Bx},${By} ${Cx},${Cy}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
      />

      {/* α_C – pomarańczowy łuk + podpis */}
      <path d={arcC.d} stroke="#a855f7" strokeWidth="3" fill="none" />
      <LabelAngle x={arcC.midx} y={arcC.midy} pre="\alpha" sub="C" text={labelC} color="#a855f7" />

      {/* P₁ – fiolet (ramiona + łuk + punkt + literka) */}
      {showP1 && (
        <>
          <line x1={P1.x} y1={P1.y} x2={P1e1[0]} y2={P1e1[1]} stroke="#f59e0b" strokeWidth="2" />
          <line x1={P1.x} y1={P1.y} x2={P1e2[0]} y2={P1e2[1]} stroke="#f59e0b" strokeWidth="2" />
          <path d={arcP1.d} stroke="#f59e0b" strokeWidth="3" fill="none" />
          <circle cx={P1.x} cy={P1.y} r="4" fill="#f59e0b" />
          <MathPointLabel x={P1out.x} y={P1out.y} math="P_1" />
        </>
      )}

      {/* P₂ – zielony (ramiona + łuk + punkt + literka) */}
      {showP2 && (
        <>
          <line x1={P2.x} y1={P2.y} x2={P2e1[0]} y2={P2e1[1]} stroke="#f59e0b" strokeWidth="2" />
          <line x1={P2.x} y1={P2.y} x2={P2e2[0]} y2={P2e2[1]} stroke="#f59e0b" strokeWidth="2" />
          <path d={arcP2.d} stroke="#f59e0b" strokeWidth="3" fill="none" />
          <circle cx={P2.x} cy={P2.y} r="4" fill="#f59e0b" />
          <MathPointLabel x={P2out.x} y={P2out.y} math="P_2" />
        </>
      )}

      {/* wierzchołki A,B,C */}
      <circle cx={Ax} cy={Ay} r="4" fill="currentColor" />
      <circle cx={Bx} cy={By} r="4" fill="currentColor" />
      <circle cx={Cx} cy={Cy} r="4" fill="currentColor" />

      <MathPointLabel x={Aout.x} y={Aout.y} math="A" />
      <MathPointLabel x={Bout.x} y={Bout.y} math="B" />
      <MathPointLabel x={Cout.x} y={Cout.y} math="C" />
    </svg>
  );
}
